"use client";

import { formatDate } from "@/core/utils/formatters";
import { useTranslation } from "react-i18next";
import { useInsightsExportPdf } from "../hooks/useInsightsExportPdf";
import { useReportTransforms } from "../hooks/useReportTransforms";
import { ReportViewerProps } from "../types";
import { ReportViewerCreditConsumption } from "./report-viewer-credit-consumption";
import { ReportViewerHeaderActions } from "./report-viewer-header-actions";
import { ReportViewerInsightsList } from "./report-viewer-insights-list";
import { ReportViewerOverviewCard } from "./report-viewer-overview-card";
import { ReportViewerRankingList } from "./report-viewer-ranking-list";
import { ReportViewerReportHeader } from "./report-viewer-report-header";
import { ReportViewerReservationsEvolution } from "./report-viewer-reservations-evolution";
import { ReportViewerStatusAndCheckin } from "./report-viewer-status-and-checkin";
import { ReportViewerSummaryList } from "./report-viewer-summary-list";
import { ReportViewerTypeDistribution } from "./report-viewer-type-distribution";
export function ReportViewer({
  filters,
  reportData,
  onClose,
}: ReportViewerProps) {
  const [translate] = useTranslation(["insights"]);
  const filename = `relatorio-wos-manager-${filters.startDate}-${filters.endDate}.pdf`;
  const { isGeneratingPDF, generate } = useInsightsExportPdf(filename);
  const {
    spaceDistributionData,
    creditConsumptionData,
    spaceRankingData,
    reservationEvolutionData,
    cancellationData,
    checkinData,
  } = useReportTransforms(reportData);

  const handlePrint = () => window.print();

  return (
    <div className="overflow-auto fixed inset-0 z-50 bg-background">
      <div className="p-6 mx-auto max-w-4xl">
        {/* Header */}
        <div className="flex justify-between items-center mb-6 print:hidden">
          <div>
            <h1 className="text-2xl font-bold">
              {translate("report-viewer-preview-title")}
            </h1>
            <p className="text-muted-foreground">
              {translate("report-viewer-preview-period")}:{" "}
              {formatDate(filters.startDate)}
              {" - "}
              {formatDate(filters.endDate)}
            </p>
          </div>
          <ReportViewerHeaderActions
            isGeneratingPDF={isGeneratingPDF}
            onDownloadPDF={generate}
            onPrint={handlePrint}
            onClose={onClose}
          />
        </div>

        {/* Report Content */}
        <div
          className="p-8 space-y-8 text-black rounded-lg border bg-tertiary"
          data-report-content
        >
          <ReportViewerReportHeader filters={filters} />
          <ReportViewerSummaryList />
          <ReportViewerOverviewCard reportData={reportData} />
          <ReportViewerRankingList data={spaceRankingData} />
          <ReportViewerReservationsEvolution
            data={reservationEvolutionData}
            highest={reportData.peaks.highest_peak}
            lowest={reportData.peaks.lowest_peak}
          />
          <ReportViewerStatusAndCheckin
            cancellationData={cancellationData}
            checkinData={checkinData}
            totals={{
              confirmed:
                reportData.status_and_checkin.status_reservations.confirmed
                  .count,
              canceled:
                reportData.status_and_checkin.status_reservations.canceled
                  .count,
              checkinTruePercent:
                reportData.status_and_checkin.checkins.checkin_true.percent,
              checkinFalsePercent:
                reportData.status_and_checkin.checkins.checkin_false.percent,
            }}
          />
          <ReportViewerTypeDistribution data={spaceDistributionData} />
          <ReportViewerCreditConsumption
            data={creditConsumptionData}
            totals={{
              total_utilizations:
                reportData.consumption_of_credits.total_utilizations,
              average_by_group:
                reportData.consumption_of_credits.average_by_group,
              highest_group_credits:
                reportData.consumption_of_credits.highest_consumption_group
                  ?.credits ?? 0,
            }}
          />
          <ReportViewerInsightsList insights={reportData.automatic_insights} />
        </div>
      </div>
    </div>
  );
}
